import { useMemo } from 'react';
import { measuredHistorySummary } from './historySummary';
import { formatHistoryNumeric } from '../twin/telemetry/history';

export function RfimsHistorySummary({
  points,
  days,
  subsystemId,
  dataId,
}: {
  points: { t: number; v: number }[];
  days: number;
  subsystemId: string;
  dataId: string;
}) {
  const summary = useMemo(() => measuredHistorySummary(points, days), [points, days]);
  const discrete = ['door', 'relay', 'leak'].includes(dataId);
  const fmt = (value:number)=>formatHistoryNumeric(subsystemId,dataId,value);
  const utc = (t: number) => new Date(t).toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  if (!summary.count) return null;
  return (
    <details className="rounded-control border border-hairline p-2 text-[10px] text-ink-3">
      <summary className="cursor-pointer text-ink-2">
        Measured series summary · {summary.count} returned points
      </summary>
      <p className="mt-2">
        {utc(summary.first!)} to {utc(summary.last!)}
      </p>
      <p className="mt-1">
        {discrete
          ? `${summary.observedChanges} observed changes between adjacent returned values.`
          : `Returned minimum: ${fmt(summary.min!)} · maximum: ${fmt(summary.max!)}`}
      </p>
      <p className="mt-1">
        {summary.gaps.length
          ? `${summary.gaps.length} internal gaps in returned data. Largest: ${(Math.max(...summary.gaps.map((g) => g.durationMs)) / 3_600_000).toFixed(1)} h.`
          : 'No internal gaps longer than the aggregation allowance.'}
      </p>
      {summary.gaps.length > 0 && (
        <ul className="mt-1 space-y-1">
          {summary.gaps
            .slice()
            .sort((a, b) => b.durationMs - a.durationMs)
            .slice(0, 3)
            .map((g) => (
              <li key={g.start}>
                {utc(g.start)} → {utc(g.end)}
              </li>
            ))}
        </ul>
      )}
      <p className="mt-2">
        Summaries use returned aggregates and session samples. Short events and sample gaps inside a window
        may be hidden. Missing data is not proof of equipment downtime; withheld legacy intervals are excluded
        from gap counts.
      </p>
    </details>
  );
}
